import isNil from "lodash/isNil";

import { Bundle, Client, Invoice, PaymentToken } from "@prisma/client";

import { HttpException } from "exceptions/HttpException";

import { PAYMENT_FIATS } from "enums/paymentTokens";
import { STORE_VERSIONS } from "enums/storeTypes";
import { PRODUCT_TYPES } from "enums/productTypes";

import {
  BACKWARD_COMPATIBILITY_PRINTS_NUMBER,
  FIAT_FEE,
  PRODUCT_MIN_PRICES,
  PRODUCT_PRINT_PRICE,
} from "config/store";

import { roundNumber } from "utils/formatter";

import MetadataBuilder from "services/metadata/MetadataBuilder";
import { PaymentDetail } from "services/store/PaymentDetail.interface";
import StoreFeeService from "services/store/StoreFeeService";

class BundlePrice {
  private filteredPaymentDetails: PaymentDetail[] | null = null;
  constructor(
    private readonly bundle: Pick<
      Bundle,
      "metadata" | "model" | "paymentDetails"
    > & {
      client: Pick<Client, "storeType" | "isCreatedByForm"> & {
        invoice: Pick<Invoice, "country"> | null;
      };
    },
    private readonly paymentTokens?: Omit<
      PaymentToken,
      "createdAt" | "updatedAt"
    >[],
  ) {}

  getRefPrice(isFiat = false, itemsAmount = 1): number | null {
    if (!this.bundle.metadata) {
      return null;
    }
    const refPaymentDetail = this.refPaymentDetails();
    const { metadataType } = new MetadataBuilder().getAttributes(
      this.bundle.metadata,
    );

    return BundlePrice.calculateRefPrice(
      refPaymentDetail.refPrice,
      this.bundle.client,
      metadataType || undefined,
      isFiat,
      this.bundle.model?.printsNumber ?? undefined,
      itemsAmount,
    );
  }

  getRefToken(): PAYMENT_FIATS {
    const refPaymentDetail = this.refPaymentDetails();

    return refPaymentDetail.refToken ?? PAYMENT_FIATS.USD;
  }

  getFilterPaymentDetails(): PaymentDetail[] {
    try {
      if (this.filteredPaymentDetails) {
        return this.filteredPaymentDetails;
      }
      if (!this.paymentTokens) {
        this.filteredPaymentDetails = this.bundle.paymentDetails;

        return this.filteredPaymentDetails;
      }

      const allowedTokens = this.paymentTokens.map(
        (paymentToken) => paymentToken.name,
      );

      this.filteredPaymentDetails = this.bundle.paymentDetails.filter(
        (paymentDetail) => allowedTokens.includes(paymentDetail.paymentToken),
      );

      return this.filteredPaymentDetails;
    } catch {
      throw new HttpException(
        "We are not supporting any Payment tokens on given network",
      );
    }
  }

  static calculateRefPrice(
    refPrice: number | null | undefined,
    client: Pick<Client, "storeType" | "isCreatedByForm"> & {
      invoice: Pick<Invoice, "country"> | null;
    },
    metadataType: PRODUCT_TYPES | string = PRODUCT_TYPES.T_SHIRT,
    isFiat = false,
    printsNumber = BACKWARD_COMPATIBILITY_PRINTS_NUMBER,
    itemsAmount = 1,
  ): number | null {
    const isUniqlyStore = client.storeType === STORE_VERSIONS.UNIQLY;
    const PRODUCTION_COST =
      isUniqlyStore && client.isCreatedByForm
        ? roundNumber(
            PRODUCT_MIN_PRICES[metadataType] +
              PRODUCT_PRINT_PRICE * printsNumber,
          )
        : 0;

    if (isFiat && !isNil(refPrice) && client.isCreatedByForm) {
      const sum =
        itemsAmount *
        (refPrice +
          StoreFeeService.getUniqlyFee(refPrice, client.invoice?.country) +
          PRODUCTION_COST);
      return Math.ceil(sum + sum * (FIAT_FEE / 100));
    } else if (isFiat && !isNil(refPrice) && !client.isCreatedByForm) {
      const sum = itemsAmount * (refPrice + PRODUCTION_COST);
      return Math.ceil(sum + sum * (FIAT_FEE / 100));
    }

    return !isNil(refPrice) && client.isCreatedByForm
      ? roundNumber(
          itemsAmount *
            (refPrice +
              StoreFeeService.getUniqlyFee(
                itemsAmount * refPrice,
                client.invoice?.country,
              ) +
              PRODUCTION_COST),
          2,
        )
      : !isNil(refPrice)
      ? itemsAmount * refPrice
      : null;
  }

  private refPaymentDetails(): PaymentDetail {
    try {
      const paymentDetail = this.getFilterPaymentDetails().find(
        (detail) =>
          detail.refToken === PAYMENT_FIATS.USD ||
          detail.refToken === PAYMENT_FIATS.PLN,
      );

      if (!paymentDetail) {
        throw new Error("Payment detail not found");
      }

      return paymentDetail;
    } catch {
      throw new HttpException("Payment is not supported!");
    }
  }
}

export default BundlePrice;
